import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

// CSRF token management
function getCsrfToken() {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrf_token="))
    ?.split("=")[1];
}

function setCsrfToken(token) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `csrf_token=${token}; Path=/; SameSite=Strict${secure}; Max-Age=${60 * 60 * 24 * 30}`;
}

// Attach CSRF token to all API requests
api.interceptors.request.use((config) => {
  const token = getCsrfToken();
  if (token) {
    config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// Input sanitization
function sanitizeInput(value) {
  if (typeof value !== "string") return value;
  return value.replace(/[<>\"'&]/g, (char) => {
    const map = { "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#x27;", "&": "&amp;" };
    return map[char] || char;
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: skip /me check if returning from Google OAuth
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip /me call if there's no evidence of an existing session
    // This prevents 401 network errors that Lighthouse penalizes
    const hasAuthCookie = document.cookie.includes("session=") || document.cookie.includes("csrf_token=");
    const hasPersistentAuth = localStorage.getItem("auth_persistent") === "true";
    if (!hasAuthCookie && !hasPersistentAuth) {
      setLoading(false);
      setUser(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", {
        email: sanitizeInput(email),
        password: sanitizeInput(password),
      });
      if (data.csrf_token) {
        setCsrfToken(data.csrf_token);
      }
      // Handle remember me
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      if (rememberMe) {
        localStorage.setItem("auth_persistent", "true");
      }
      // Track activity for session timeout
      localStorage.setItem("lastActivity", Date.now().toString());
      setUser(data);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", {
        email: sanitizeInput(email),
        password: sanitizeInput(password),
        name: sanitizeInput(name),
      });
      if (data.csrf_token) {
        setCsrfToken(data.csrf_token);
      }
      localStorage.setItem("lastActivity", Date.now().toString());
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    // Clear CSRF token
    document.cookie = "csrf_token=; Path=/; Max-Age=0";
    localStorage.removeItem("lastActivity");
    localStorage.removeItem("auth_persistent");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
